/**
 * BulkAddLookupAddsBlock — Block 3 of the preview pane (Block 6.E).
 *
 * Aggregated list of new subcategory + service-area slugs referenced
 * by the paste. Single explicit checkbox: "I have reviewed and approve
 * these additions." Per ADR-014 §T1 + ADR-017 §F11.
 */

import type { ReactNode } from 'react';
import type { BulkAddPendingLookupAdd } from '@/lib/types';

export interface BulkAddLookupAddsBlockProps {
  pendingAdds: ReadonlyArray<BulkAddPendingLookupAdd>;
  approved: boolean;
  onToggleApproved: (next: boolean) => void;
}

export function BulkAddLookupAddsBlock({
  pendingAdds,
  approved,
  onToggleApproved,
}: BulkAddLookupAddsBlockProps): ReactNode {
  if (pendingAdds.length === 0) return null;

  const subcategories = pendingAdds.filter((a) => a.type === 'subcategory');
  const serviceAreas = pendingAdds.filter((a) => a.type === 'service_area');

  return (
    <div className="border-2 border-amber-400 bg-amber-50 rounded-md p-3 mb-3">
      <div className="text-sm font-semibold mb-2">
        Lookup taxonomy additions ({pendingAdds.length})
      </div>
      {subcategories.length > 0 && (
        <ul className="text-sm mb-2 list-disc pl-5 space-y-0.5">
          {subcategories.map((add) => (
            <li key={add.slug}>
              New subcategory: <span className="font-semibold">"{add.display_name}"</span>{' '}
              <span className="text-xs text-ink-soft">
                (slug: <span className="font-mono">{add.slug}</span>; referenced by {add.referenced_by_row_count} row{add.referenced_by_row_count === 1 ? '' : 's'})
              </span>
            </li>
          ))}
        </ul>
      )}
      {serviceAreas.length > 0 && (
        <ul className="text-sm mb-2 list-disc pl-5 space-y-0.5">
          {serviceAreas.map((add) => (
            <li key={add.slug}>
              New service area: <span className="font-semibold">"{add.display_name}"</span>{' '}
              <span className="text-xs text-ink-soft">
                (slug: <span className="font-mono">{add.slug}</span>
                {add.area_type && (
                  <>; type: <span className="font-mono">{add.area_type}</span></>
                )}
                {add.state_code && (
                  <>; state: <span className="font-mono">{add.state_code}</span></>
                )}
                ; referenced by {add.referenced_by_row_count} row{add.referenced_by_row_count === 1 ? '' : 's'})
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-xs text-ink-soft mb-2">
        Approving adds these categories everywhere, for every organization.
      </div>
      <label className="flex items-baseline gap-2 cursor-pointer min-h-11">
        <input
          type="checkbox"
          checked={approved}
          onChange={(e) => onToggleApproved(e.target.checked)}
          aria-label="Approve all lookup taxonomy additions"
          className="mt-0.5"
        />
        <span className="text-sm font-semibold">
          I have reviewed and approve these additions.
        </span>
      </label>
    </div>
  );
}
